import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { PageHeader } from "../components/layout";
import { Button, Card, StatusIndicator, type StatusTone } from "../components/ui";
import { AdvancedJson, KeyVal, SeverityBadge, StateTag, formatAge, shortHash } from "../components/phase3";
import { ApiUnavailableState, EmptyState, LoadingState, NotConfiguredState } from "../components/states";
import { useAppContext } from "../hooks/useAppContext";
import { BlueprintContentPreview } from "./Blueprints";
import {
  api,
  apiConfigured,
  ApiError,
  type BlueprintVersion,
  type BlueprintView,
  type TemplateSummary,
  type ValidationOutcome,
  type VersionStatus,
} from "../lib/api";

function toMessage(e: unknown): string {
  return e instanceof ApiError ? e.message : "Something went wrong. Please try again.";
}

function versionTone(status: VersionStatus): StatusTone {
  switch (status) {
    case "PUBLISHED":
      return "ok";
    case "VALID":
      return "ok";
    case "ARCHIVED":
      return "neutral";
    default:
      return "warn";
  }
}

export function BlueprintDetail(): JSX.Element {
  const { blueprintId = "" } = useParams();
  const ctx = useAppContext();
  const [blueprint, setBlueprint] = useState<BlueprintView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [templates, setTemplates] = useState<TemplateSummary[]>([]);

  // No list-versions endpoint exists, so we track versions created / loaded this
  // session in memory (no persistence) and let the user load any version by id.
  const [versions, setVersions] = useState<BlueprintVersion[]>([]);
  const [selected, setSelected] = useState<BlueprintVersion | null>(null);
  const [validation, setValidation] = useState<ValidationOutcome | null>(null);
  const [busy, setBusy] = useState(false);
  const [seedKey, setSeedKey] = useState("");
  const [lookupId, setLookupId] = useState("");

  const tenantId = ctx.tenantId;

  useEffect(() => {
    if (!tenantId || !blueprintId) return;
    api
      .getBlueprint(tenantId, blueprintId)
      .then(setBlueprint)
      .catch((e) => setError(toMessage(e)));
    api.listTemplates().then((r) => setTemplates(r.templates)).catch(() => setTemplates([]));
  }, [tenantId, blueprintId]);

  const trackVersion = (v: BlueprintVersion) => {
    setSelected(v);
    setValidation(null);
    setVersions((prev) => {
      const rest = prev.filter((x) => x.id !== v.id);
      return [v, ...rest];
    });
  };

  const createDraft = async () => {
    if (!tenantId || !seedKey) return;
    setBusy(true);
    setError(null);
    try {
      const tpl = await api.getTemplate(seedKey);
      const v = await api.createVersion(tenantId, blueprintId, { content: tpl.content, notes: `Draft seeded from ${tpl.key}` });
      trackVersion(v);
      setSeedKey("");
    } catch (e) {
      setError(toMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const loadVersion = async () => {
    if (!tenantId || !lookupId.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const v = await api.getVersion(tenantId, blueprintId, lookupId.trim());
      trackVersion(v);
      setLookupId("");
    } catch (e) {
      setError(toMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const act = async (fn: () => Promise<BlueprintVersion>) => {
    setBusy(true);
    setError(null);
    try {
      trackVersion(await fn());
    } catch (e) {
      setError(toMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const validate = async () => {
    if (!tenantId || !selected) return;
    setBusy(true);
    setError(null);
    try {
      const outcome = await api.validateVersion(tenantId, blueprintId, selected.id);
      setValidation(outcome);
      trackVersion(outcome.version);
    } catch (e) {
      setError(toMessage(e));
    } finally {
      setBusy(false);
    }
  };

  if (!apiConfigured)
    return (
      <NotConfiguredState title="Control API URL not configured">
        Set <code>VITE_CONTROL_API_URL</code> to manage blueprints.
      </NotConfiguredState>
    );

  if (!tenantId)
    return (
      <>
        <PageHeader title="Blueprint" />
        <EmptyState title="No workspace selected">
          <Link to="/app/blueprints">← Back to the blueprint library</Link>
        </EmptyState>
      </>
    );

  const archived = versions.filter((v) => v.status === "ARCHIVED");
  const active = versions.filter((v) => v.status !== "ARCHIVED");

  return (
    <>
      <PageHeader title={blueprint?.name ?? "Blueprint"}>
        <p>
          <Link to="/app/blueprints">← Blueprint library</Link> · This is a <StateTag state="desired" /> definition.
        </p>
      </PageHeader>

      {error ? <ApiUnavailableState detail={error} /> : null}
      {!blueprint ? <LoadingState label="Loading blueprint…" /> : null}

      {blueprint ? (
        <Card title="Details">
          <div className="kv-grid">
            <KeyVal k="Name" v={blueprint.name} />
            <KeyVal k="Community" v={blueprint.communityType ?? "—"} />
            <KeyVal k="Created" v={formatAge(blueprint.createdAt)} />
            <KeyVal k="Updated" v={formatAge(blueprint.updatedAt)} />
          </div>
          {blueprint.description ? <p>{blueprint.description}</p> : null}
        </Card>
      ) : null}

      <Card title="Create a draft version" description="Seed a new draft from a template, then validate and publish it.">
        <div className="row">
          <select className="field__input" value={seedKey} onChange={(e) => setSeedKey(e.target.value)} aria-label="Seed template">
            <option value="">Select template to seed…</option>
            {templates.map((t) => (
              <option key={t.key} value={t.key}>
                {t.name}
              </option>
            ))}
          </select>
          <Button variant="primary" disabled={busy || !seedKey} onClick={createDraft}>
            Create draft
          </Button>
          <span className="muted">or</span>
          <input className="field__input" value={lookupId} onChange={(e) => setLookupId(e.target.value)} placeholder="Load version by id" aria-label="Version id" />
          <Button variant="secondary" disabled={busy || lookupId.trim().length < 1} onClick={loadVersion}>
            Load version
          </Button>
        </div>
      </Card>

      {active.length > 0 ? (
        <Card title="Version history (this session)">
          <div className="op-list">
            {active.map((v) => (
              <button key={v.id} className="list-row" onClick={() => trackVersion(v)}>
                <StatusIndicator tone={versionTone(v.status)} label={v.status} />
                <strong>{v.version}</strong>
                <code>{shortHash(v.contentHash)}</code>
                <span className="muted">{formatAge(v.createdAt)}</span>
              </button>
            ))}
          </div>
        </Card>
      ) : null}

      {archived.length > 0 ? (
        <Card title={`Archived versions (${archived.length})`}>
          <div className="op-list">
            {archived.map((v) => (
              <button key={v.id} className="list-row list-row--muted" onClick={() => trackVersion(v)}>
                <StatusIndicator tone="neutral" label="ARCHIVED" />
                <strong>{v.version}</strong>
                <code>{shortHash(v.contentHash)}</code>
              </button>
            ))}
          </div>
        </Card>
      ) : null}

      {selected ? (
        <Card title={`Version ${selected.version}`}>
          <div className="kv-grid">
            <KeyVal k="Status" v={<StatusIndicator tone={versionTone(selected.status)} label={selected.status} />} />
            <KeyVal k="Content hash" v={shortHash(selected.contentHash)} mono />
            <KeyVal k="Validated" v={selected.validatedAt ? formatAge(selected.validatedAt) : "—"} />
            <KeyVal k="Published" v={selected.publishedAt ? formatAge(selected.publishedAt) : "—"} />
          </div>

          <div className="row" style={{ margin: "0.75rem 0" }}>
            <Button variant="secondary" disabled={busy || selected.status === "ARCHIVED"} onClick={validate}>
              Validate
            </Button>
            <Button
              variant="primary"
              disabled={busy || selected.status === "ARCHIVED" || selected.status === "DRAFT"}
              title={selected.status === "DRAFT" ? "Validate the draft before publishing" : undefined}
              onClick={() => tenantId && act(() => api.publishVersion(tenantId, blueprintId, selected.id))}
            >
              Publish
            </Button>
            <Button
              variant="ghost"
              disabled={busy || selected.status === "ARCHIVED"}
              onClick={() => tenantId && act(() => api.archiveVersion(tenantId, blueprintId, selected.id))}
            >
              Archive
            </Button>
          </div>

          {validation ? <ValidationView outcome={validation} /> : null}

          {selected.content ? (
            <>
              <h4 className="subhead">Draft contents</h4>
              <BlueprintContentPreview content={selected.content} />
              <AdvancedJson value={selected.content} />
            </>
          ) : (
            <p className="muted">This version's content is not included in the response.</p>
          )}
        </Card>
      ) : null}
    </>
  );
}

function ValidationView({ outcome }: { outcome: ValidationOutcome }): JSX.Element {
  return (
    <div className={outcome.valid ? "validation validation--ok" : "validation validation--bad"}>
      <StatusIndicator tone={outcome.valid ? "ok" : "danger"} label={outcome.valid ? "Validation passed" : "Validation failed"} />
      {outcome.errors.length > 0 ? (
        <div className="finding-list" style={{ marginTop: "0.5rem" }}>
          {outcome.errors.map((err, i) => (
            <div key={i} className="finding-row">
              <div className="op-row__head">
                <SeverityBadge severity="ERROR" />
                <code>{err.path || "(root)"}</code>
              </div>
              <p className="finding-row__plain">{err.message}</p>
            </div>
          ))}
        </div>
      ) : (
        <p className="muted" style={{ marginTop: "0.5rem" }}>
          No problems found. This version is ready to publish.
        </p>
      )}
    </div>
  );
}
